// Create-club form state + submit. Mirrors screens/HomeScreen.js handlePickLogo/Gallery/PDF (:433-560)
// and handleSubmitClubRequest (:596-790): same validation order, same upload order
// (logo -> PDFs in parallel -> gallery in parallel), same createClubRequest payload.
import { useCallback, useEffect, useRef, useState } from "react";
import { CLUB_APPLICATION_LIMITS } from "../../../../firebase-functions/schema/clubApplication";
import { ClubService } from "../../../../services/clubService";
import { UploadService } from "../../../../services/uploadService";
import { normalizeLink } from "../../../../utils/clubLinks";
import { compressForAvatar, compressForUpload, pdfToAsset, releaseUri } from "../../lib/files";
import { useToast } from "../Toast";
import { msg } from "./clubUtils";

export const MAX_GALLERY_IMAGES = 8;
const EMPTY_FORM = { name: "", description: "", links: [], pdfs: [], gallery: [], logo: null, category: null, category_id: null };

// Anything the user would lose by leaving the page.
const hasContent = (f) => !!(f.category_id || f.name.trim() || f.description.trim() || f.logo || f.links.length || f.pdfs.length || f.gallery.length);

// Same order and copy as HomeScreen.js; returns { field, message } for the first problem, else null.
function firstInvalid(f) {
  if (!f.category_id) return { field: "category", message: "Please select a club category" };
  if (!f.name.trim()) return { field: "name", message: "Please enter a club name" };
  if (!f.description.trim()) return { field: "description", message: "Please enter a club description" };
  return null;
}

export default function useCreateClub(user) {
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { notes: string[] } after a successful submit
  const [errors, setErrors] = useState({}); // field -> inline message, from the last submit attempt
  const formRef = useRef(form);
  formRef.current = form;
  const gen = useRef(0);

  const submittingRef = useRef(false);
  const aliveRef = useRef(false);
  const freeBlobs = () => {
    const f = formRef.current;
    releaseUri(f.logo);
    f.gallery.forEach(releaseUri);
    f.pdfs.forEach((p) => releaseUri(p.uri));
  };

  // Free blob: URIs on unmount, unless a submit is still reading them (its finally frees them).
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (!submittingRef.current) freeBlobs();
    };
  }, []);

  // Warn before closing or reloading the tab mid-submit or with unsaved input.
  const dirty = !done && hasContent(form);
  useEffect(() => {
    if (!submitting && !dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [submitting, dirty]);

  // Editing a field clears its inline error.
  const patch = useCallback((fields) => {
    setForm((p) => ({ ...p, ...fields }));
    setErrors((e) => {
      const keys = Object.keys(fields).map((k) => (k === "category_id" ? "category" : k)).filter((k) => e[k]);
      if (!keys.length) return e;
      const next = { ...e };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  const pickLogo = async (files) => {
    const file = files?.[0];
    if (!file) return;
    try {
      setCompressing(true);
      const uri = await compressForAvatar(file);
      setForm((p) => { releaseUri(p.logo); return { ...p, logo: uri }; });
    } catch (e) {
      toast(msg(e, "Failed to pick image"), "alert");
    } finally {
      setCompressing(false);
    }
  };

  const addGallery = async (files) => {
    const slots = MAX_GALLERY_IMAGES - formRef.current.gallery.length;
    if (slots <= 0) { toast(`You can add up to ${MAX_GALLERY_IMAGES} photos.`, "info"); return; }
    const g = gen.current;
    setCompressing(true);
    const uris = [];
    let failed = 0;
    for (const f of files.slice(0, slots)) {
      try { uris.push(await compressForUpload(f)); } catch { failed += 1; }
    }
    if (g === gen.current) {
      if (failed) toast(`${failed} photo(s) couldn't be processed. Try smaller photos.`, "alert");
      setForm((p) => ({ ...p, gallery: [...p.gallery, ...uris].slice(0, MAX_GALLERY_IMAGES) }));
    } else {
      uris.forEach(releaseUri);
    }
    setCompressing(false);
  };

  const addPdfs = (files) => {
    const max = CLUB_APPLICATION_LIMITS.list;
    const room = Math.max(0, max - formRef.current.pdfs.length);
    if (files.length > room) toast(`Up to ${max} documents.`, "info");
    if (room === 0) return;
    const picked = files.slice(0, room).map(pdfToAsset);
    setForm((p) => ({ ...p, pdfs: [...p.pdfs, ...picked].slice(0, max) }));
  };
  const removeAt = (key, i) => setForm((p) => {
    const gone = p[key][i];
    releaseUri(key === "pdfs" ? gone?.uri : gone);
    return { ...p, [key]: p[key].filter((_, idx) => idx !== i) };
  });
  const removeLogo = () => setForm((p) => { releaseUri(p.logo); return { ...p, logo: null }; });

  // Resolves to the invalid field's key ("category" | "name" | "description") so the page can focus it.
  const submit = async () => {
    if (submitting || compressing) return null;
    const f = formRef.current;
    const bad = firstInvalid(f);
    if (bad) {
      setErrors({ [bad.field]: bad.message });
      toast(bad.message, "alert");
      return bad.field;
    }
    setErrors({});
    if (!user) { toast("You must be logged in to create a club", "alert"); return null; }

    setSubmitting(true);
    submittingRef.current = true;
    try {
      let logoUrl = null;
      if (f.logo) {
        try {
          const r = await UploadService.uploadImageViaREST(f.logo, `club-logos/${user.uid}`, `club-logo-${Date.now()}.jpg`);
          logoUrl = r.data?.url || null;
        } catch {
          toast("Failed to upload logo. Request will be submitted without logo.", "alert");
        }
      }

      const pdfUrls = [];
      const failedPdfNames = [];
      if (f.pdfs.length > 0) {
        const ts = Date.now();
        const results = await Promise.allSettled(
          f.pdfs.map((pdf, i) => UploadService.uploadPDFViaREST(pdf.uri, "club-documents", `club-doc-${ts}-${i}-${pdf.name || "document.pdf"}`))
        );
        results.forEach((r, i) => {
          if (r.status === "fulfilled" && r.value?.data?.url) pdfUrls.push(r.value.data.url);
          else failedPdfNames.push(f.pdfs[i].name || "document");
        });
      }

      const galleryUrls = [];
      let galleryFailures = 0;
      if (f.gallery.length > 0) {
        const ts = Date.now();
        const path = `club-gallery-staging/${user.uid}`;
        const results = await Promise.allSettled(f.gallery.map((uri, i) => UploadService.uploadImageViaREST(uri, path, `${ts}_${i}.jpg`)));
        results.forEach((r) => {
          if (r.status === "fulfilled" && r.value?.data?.url) galleryUrls.push(r.value.data.url);
          else galleryFailures += 1;
        });
      }

      const { error } = await ClubService.createClubRequest({
        name: f.name.trim(),
        description: f.description.trim(),
        links: f.links.map(normalizeLink).filter((l) => l.url.trim()).map((l) => ({ url: l.url.trim(), name: l.name.trim(), is_members_only: false })),
        pdfs: pdfUrls,
        gallery: galleryUrls,
        logo_url: logoUrl,
        userId: user.uid,
        category: f.category,
        category_id: f.category_id,
      });
      if (error) throw error;

      const notes = [];
      if (failedPdfNames.length) notes.push(`${pdfUrls.length}/${f.pdfs.length} documents uploaded successfully (failed: ${failedPdfNames.join(", ")}).`);
      if (galleryFailures) notes.push(`${galleryUrls.length}/${f.gallery.length} gallery photos uploaded successfully.`);
      gen.current += 1;
      setDone({ notes });
      setForm(EMPTY_FORM);
      releaseUri(f.logo);
      f.gallery.forEach(releaseUri);
      f.pdfs.forEach((p) => releaseUri(p.uri));
    } catch (e) {
      toast(msg(e, "Failed to submit club request. Please try again."), "alert");
    } finally {
      submittingRef.current = false;
      if (aliveRef.current) setSubmitting(false);
      else freeBlobs();
    }
    return null;
  };

  return { form, patch, setForm, compressing, submitting, done, dirty, errors, pickLogo, removeLogo, addGallery, addPdfs, removeAt, submit };
}
